import { useMemo, useState, useEffect, useRef } from "react";
import { DateTime } from "@components/common/calendar/date-range-picker/Interfaces";
import moment from "moment";
import _ from "lodash";
import { CalendarModeRangeType } from "@custom-types/Types";
import { HVIcon, HVLoader } from "@repo/HVComponents";
import { CALENDAR_MODE_RANGE_TYPE, FORMAT } from "@resources/Constants";
import { useCurrentTimezone } from "@hooks/useCurrentTimezone";
import { DateRangePicker } from "@components/common/calendar/date-range-picker";
import { useAppSelector } from "@configs/Redux";
import { useLang } from "@hooks/useLang";
import { updateDateTimeZone } from "@utils/Widget";
import * as S from "../LeftMenu.styled";
import { ICON } from "../../../../constants/icons";

interface CalendarButtonProps {
  dateTime: DateTime;
  onChange: (date: {
    start: string;
    end: string;
    mode: CalendarModeRangeType;
    comparisonStart?: string;
    comparisonEnd?: string;
  }) => void;
  isToday?: boolean;
  showLeft?: boolean;
  currentTimeZone?: string;
  setCurrentTimeZone?: (currentTimeZone: string) => void;
  currentUserTimeZone?: string;
  utc?: string;
  showMode?: boolean;
  isLoading?: boolean;
  isReport?: boolean;
  isDisabled?: boolean;
  isCalendar?: boolean;
}
export const CalendarButton = (props: CalendarButtonProps) => {
  const {
    dateTime,
    currentTimeZone,
    setCurrentTimeZone,
    currentUserTimeZone,
    utc,
    showMode,
    isLoading,
    isReport,
    isDisabled,
    isCalendar
  } = props;
  const [isShowCalendar, setIsShowCalendar] = useState(false);
  const { translate } = useLang();
  const { convertByTimezone } = useCurrentTimezone();
  const { lang } = useAppSelector((state) => state.system);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const timeZone = (currentTimeZone as string) || moment.tz.guess();
  const commonModes = [
    "custom",
    "last_24_hours",
    "last_7_days",
    "last_30_days",
    "last_90_days",
    "last_180_days",
    "last_365_days",
    "yesterday",
    "day_before_yesterday"
  ] as CalendarModeRangeType[];

  const dates = useMemo(() => {
    return updateDateTimeZone(
      dateTime,
      currentTimeZone || timeZone,
      !props.isToday,
      isReport
    );
  }, [dateTime, currentTimeZone]);

  const period = useMemo(() => {
    if (dateTime) {
      const range = [];
      const startMoment = moment(dates.start).tz(timeZone);
      const endMoment = moment(dates.end).tz(timeZone);
      const currentMoment = moment().tz(timeZone);
      const localLang = lang.toLocaleLowerCase();
      if (
        showMode &&
        !Number.isNaN(endMoment?.get("year")) &&
        (startMoment?.get("year") !== endMoment?.get("year") ||
          startMoment?.get("year") !== currentMoment?.get("year") ||
          endMoment?.get("year") !== currentMoment?.get("year"))
      ) {
        range.push(
          startMoment?.locale(localLang).format(FORMAT.MONTH_DATE_YEAR_1)
        );
        range.push(
          endMoment?.locale(localLang).format(FORMAT.MONTH_DATE_YEAR_1)
        );
      } else {
        range.push(startMoment?.locale(localLang).format(FORMAT.DATE5));
        !Number.isNaN(endMoment?.get("year")) &&
          dateTime.mode !== CALENDAR_MODE_RANGE_TYPE.TODAY &&
          range.push(endMoment?.locale(localLang).format(FORMAT.DATE5));
      }

      return _.uniq(range).join(" - ");
    }
    return "Period";
  }, [dates, utc, lang]);

  const mode = useMemo(() => {
    if (dateTime) {
      return (
        <S.Mode>
          {dateTime.mode === CALENDAR_MODE_RANGE_TYPE.TODAY && <S.Circle />}{" "}
          {translate(dateTime.mode)}
        </S.Mode>
      );
    }
    return "";
  }, [dateTime, utc, lang]);

  const handleChangeDate = (selectedDate: DateTime) => {
    if (dateTime && !props.isToday) {
      // if prev date is saved, compare with changed date
      const origin = {
        start: convertByTimezone(dateTime.start),
        end: convertByTimezone(dateTime.end)
      };
      const changed = {
        start: convertByTimezone(selectedDate.start),
        end: convertByTimezone(selectedDate.end)
      };

      if (!_.isEqual(origin, changed)) {
        props.onChange(selectedDate);
      }
    } else {
      // if prev date is not saved, save changed date
      props.onChange(selectedDate);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={datePickerRef} style={{ width: isReport ? "100%" : "auto" }}>
      <S.MenuButton
        style={{
          padding: `${props.showLeft ? "0 11px 0 11px" : "0 15px 0 11px"}`,
          position: "relative"
        }}
        onClick={() => setIsShowCalendar(!isShowCalendar)}
        showLeft={props.showLeft || false}
        aria-label="Date range picker"
        disabled={isDisabled}
      >
        {showMode && mode}
        <HVIcon
          name="calendar"
          svgProps={{ ...ICON.SIZE_20, ...ICON.COLOR_STROKE }}
        />
        {isLoading ? (
          <HVLoader
            size={20}
            containerProps={{ h: "100%", justify: "center" }}
          />
        ) : (
          period && (
            <>
              <span>{period}</span>
              {utc && (
                <S.ShowTimeZone disabled={isDisabled}>{utc}</S.ShowTimeZone>
              )}
            </>
          )
        )}
        {isShowCalendar && (
          <S.LayerWrap
            onClick={(e) => e.stopPropagation()}
            showLeft={props.showLeft ?? false}
            isReport={isReport ?? false}
          >
            <DateRangePicker<DateTime>
              dateTime={dates}
              setDateTime={handleChangeDate}
              onShow={(isShow) => setIsShowCalendar(isShow)}
              yearIsLimit
              showRangeMode
              usePreview={false}
              modes={!props?.isToday ? commonModes : ["today", ...commonModes]}
              baseTime={
                !props?.isToday
                  ? convertByTimezone(
                      moment().subtract(1, "d").tz(timeZone).toISOString(true)
                    )
                  : convertByTimezone(moment().tz(timeZone).toISOString(true))
              }
              utc={utc}
              timeZone={currentTimeZone || moment.tz.guess()}
              setTimeZone={setCurrentTimeZone}
              currentUserTimeZone={currentUserTimeZone}
              isCalendar={isCalendar}
            />
          </S.LayerWrap>
        )}
      </S.MenuButton>
    </div>
  );
};
